import { useState } from "react";
import axios from "axios";
import { Mail, Phone, Instagram, Send } from "lucide-react";
import { profile } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", projectType: "", date: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Missing info",
        description: "Please fill your name, email, and a short message.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setForm(initial);
      toast({
        title: "Inquiry sent — thank you!",
        description: `I'll be in touch at ${form.email} within 48 hours.`,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong sending your message. Please try again or email directly.";
      toast({
        title: "Couldn't send",
        description: typeof msg === "string" ? msg : "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-[#EEE8DB]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5 reveal">
            <div className="text-xs tracking-[0.25em] uppercase text-[#8B6F47] mb-4">(05) Contact</div>
            <h2 className="font-display text-[48px] md:text-[72px] leading-[0.98] tracking-tight">
              Let's make
              <br />
              something <span className="italic-display">beautiful</span>.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[#1A1A1A]/70">
              Tell me about the moment, the people, or the project. I'll get back within 48 hours
              with availability and a custom quote.
            </p>

            <div className="mt-10 flex flex-col gap-5">
              <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-4 link-underline w-fit">
                <Mail size={18} className="text-[#8B6F47]" />
                <span>{profile.email}</span>
              </a>
              <a href={`tel:${profile.phone}`} className="inline-flex items-center gap-4 link-underline w-fit">
                <Phone size={18} className="text-[#8B6F47]" />
                <span>{profile.phone}</span>
              </a>
              <a
                href={profile.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-4 link-underline w-fit"
              >
                <Instagram size={18} className="text-[#8B6F47]" />
                <span>{profile.instagram}</span>
              </a>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="col-span-12 md:col-span-7 bg-[#F5F1EA] p-8 md:p-12 reveal"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Your Name" value={form.name} onChange={onChange("name")} placeholder="Jane Doe" />
              <Field label="Email" type="email" value={form.email} onChange={onChange("email")} placeholder="you@email.com" />
              <Field
                label="Project Type"
                value={form.projectType}
                onChange={onChange("projectType")}
                placeholder="Engagement, Senior, Documentary…"
              />
              <Field
                label="Tentative Date"
                type="date"
                value={form.date}
                onChange={onChange("date")}
                placeholder=""
              />
            </div>
            <div className="mt-6">
              <label className="block text-[11px] tracking-[0.25em] uppercase text-[#1A1A1A]/60 mb-2">
                Tell me about it
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                placeholder="Where, when, who, and why it matters…"
                className="w-full bg-transparent border-b border-[#1A1A1A]/30 focus:border-[#1A1A1A] outline-none py-3 font-body text-[15px] resize-none transition-colors"
              />
            </div>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-[#1A1A1A]/55">
                I reply within 48 hours. Weekends included.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary rounded-full px-7 py-3.5 text-sm tracking-wide inline-flex items-center gap-2 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Inquiry"} <Send size={15} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="block text-[11px] tracking-[0.25em] uppercase text-[#1A1A1A]/60 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-[#1A1A1A]/30 focus:border-[#1A1A1A] outline-none py-3 font-body text-[15px] transition-colors"
    />
  </div>
);

export default Contact;
